/**
 * Client API centralisé (multi-tenant A5).
 *
 * Point d'entrée unique pour tous les appels backend : injecte proprement
 * `X-Alterrego-Tenant`, `X-Alterrego-Env` et `Authorization: Bearer`, et gère le
 * 401 global (session expirée) au même endroit. Remplace les ~25 duplications de
 * `https://api.apps.alterrego.com` en dur et les headers `X-Alterrego-*` posés à
 * la main dans chaque écran.
 *
 * Défauts surchargeables au build (Vite) :
 *   - VITE_BACKEND_URL   → base backend
 *   - VITE_TENANT_ID     → tenant émis dans X-Alterrego-Tenant
 *   - VITE_ALTERREGO_ENV → env émis dans X-Alterrego-Env (défaut `live`, aligné
 *     sur config.ts / l'ancien authFetch — surtout PAS `test` pour éviter de
 *     router la prod vers la config test côté backend A4).
 */
import { getAuthToken, handleUnauthorized } from './auth';

export const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL as string) || 'https://api.apps.alterrego.com';
export const TENANT_ID = (import.meta.env.VITE_TENANT_ID as string) || 'sauf-imprevu';
export const ENV = (import.meta.env.VITE_ALTERREGO_ENV as string) || 'live';

/**
 * Headers standard tenant + env (+ Authorization sauf `auth: false`). À passer
 * tel quel à `fetch`, ou fusionner avec des headers spécifiques (Content-Type…).
 */
export function apiHeaders(options?: { auth?: boolean }): Record<string, string> {
  const headers: Record<string, string> = {
    'X-Alterrego-Tenant': TENANT_ID,
    'X-Alterrego-Env': ENV,
  };
  if (options?.auth !== false) {
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/** Construit une URL backend absolue. Laisse passer une URL déjà absolue (http). */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Wrapper `fetch` centralisé.
 * - Injecte automatiquement X-Alterrego-Tenant, X-Alterrego-Env et (sauf
 *   `auth: false`) Authorization.
 * - Sur 401 : purge la session et émet l'event global `session-expired`
 *   (bannière SessionExpiredBanner). Aucun retry.
 * - Les headers passés dans `init.headers` ont priorité sur ceux par défaut.
 */
export async function apiFetch(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<Response> {
  const { auth, headers: initHeaders, ...fetchInit } = init;
  const headers = { ...apiHeaders({ auth }), ...(initHeaders as Record<string, string> | undefined) };
  const res = await fetch(apiUrl(path), { ...fetchInit, headers });
  if (res.status === 401) handleUnauthorized();
  return res;
}
